import IconButton from 'components/IconButton';
import ModalDialog from 'components/ModalDialog';
import { useAtom } from 'jotai';
import React, { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Collapse,
  Button,
} from 'reactstrap';
import SAST2 from 'views/sast2';
import { currentChartTypeAtom } from 'views/state';

import { SASTType } from './config';
import CurrentCharts from './currentCharts';
import HistoryCharts from './historyCharts';
import {
  severityFiltersAtom,
  timeSpanAtom,
  intervalAtom,
  sastTypeAtom,
  showHistoryChartCountsAtom,
  historyChartsOpenAtom,
  currentVulnChartsOpenAtom,
  appliedRowIdsAtom,
} from './state';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import SeverityFilterDropdown from '../components/SeverityFilterDropdown';
import {
  ChartType,
  DashboardType,
  ModalDialogData,
  TooltipLabelsType,
} from '../types';

import '../styles.css';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';

import VulnerabilityMetrics from './VulnMetrics';
import AppliedHostInfo from '../components/AppliedHostInfo';

const SASTDashboard2 = () => {
  const [sastType, setSASTType] = useAtom(sastTypeAtom);
  const [currentChartType, setCurrentChartType] = useAtom(currentChartTypeAtom);

  // TOGGLE THE COLLAPSE FOR HISTORY CHARTS AND CURRENT CHARTS SECTIONS
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentVulnChartsOpen, setCurrentVulnChartsOpen] = useAtom(
    currentVulnChartsOpenAtom,
  );

  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  const listingScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (window.location.href.includes(`#${DashboardType.SAST2}`)) {
        if (listingScrollRef.current) {
          listingScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 1000);

    return () => clearTimeout(scrollTimeout);
  }, [DashboardType.SAST2]);

  function getToolTipLabels() {
    const toolTipLabels: TooltipLabelsType = [];
    if (sastType === SASTType.Bug || sastType === SASTType.All) {
      toolTipLabels.push({
        label: SASTType.Bug,
        hostsCount: null,
      });
    }

    if (sastType === SASTType.Vulnerability || sastType === SASTType.All) {
      toolTipLabels.push({
        label: SASTType.Vulnerability,
        hostsCount: null,
      });
    }
    return toolTipLabels;
  }

  return (
    <Fragment>
      <div className="container-fluid p-0">
        <nav className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light sticky-top px-0">
          <div
            className="row w-100 align-items-center"
            style={{ margin: '0px' }}
          >
            {/* First Column: Left-aligned content */}
            <Col xs="12" md="3" lg="6">
              <AppliedHostInfo appliedHostIdsAtom={appliedRowIdsAtom} />
            </Col>

            {/* Second Column: Right-aligned content */}
            <Col xs="12" md="9" lg="6" className="d-flex justify-content-end">
              <div className="d-flex flex-row">
                <SeverityFilterDropdown
                  severityFiltersAtom={severityFiltersAtom}
                />

                <IconButton
                  id="bugButton"
                  iconClass="fa fa-bugs fa-lg"
                  tooltip={sastType === SASTType.Bug ? 'Show All' : 'Bugs Only'}
                  active={sastType === SASTType.Bug}
                  onClick={() => {
                    setSASTType(
                      sastType === SASTType.Bug ? SASTType.All : SASTType.Bug,
                    );
                  }}
                />
                <IconButton
                  id="vulnerabilityButton"
                  iconClass="fa fa-bug fa-lg"
                  tooltip={
                    sastType === SASTType.Vulnerability
                      ? 'Show All'
                      : 'Vulnerability Only'
                  }
                  active={sastType === SASTType.Vulnerability}
                  onClick={() => {
                    setSASTType(
                      sastType === SASTType.Vulnerability
                        ? SASTType.All
                        : SASTType.Vulnerability,
                    );
                  }}
                />
              </div>
            </Col>
          </div>
        </nav>
        <Card>
          <CardHeader>
            <Row>
              <Col className="col-auto">
                <CardTitle tag="h4">
                  Vulnerabilities Trending History&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                  >
                    <i
                      className={
                        historyChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={historyChartsOpen}>
                  <div className="d-inline float-right">
                    <HistoryChartHeader
                      intervalAtom={intervalAtom}
                      showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                      timeSpanAtom={timeSpanAtom}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={historyChartsOpen}>
            <CardBody>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <HistoryCharts
                    tooltipLabels={getToolTipLabels()}
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </ErrorBoundary>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <CardTitle tag="h4">
                  Current Vulnerabilities&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() =>
                      setCurrentVulnChartsOpen(!currentVulnChartsOpen)
                    }
                  >
                    <i
                      className={
                        currentVulnChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={currentVulnChartsOpen}>
                  <div className="d-inline float-right">
                    <IconButton
                      id="chartTypeButton"
                      iconClass={
                        currentChartType === ChartType.Bar
                          ? 'fa fa-chart-pie fa-lg'
                          : 'fa fa-chart-bar fa-lg'
                      }
                      tooltip={
                        currentChartType === ChartType.Bar
                          ? 'Display Pie Charts'
                          : 'Display Bar Charts'
                      }
                      onClick={() => {
                        setCurrentChartType(
                          currentChartType === ChartType.Bar
                            ? ChartType.Doughnut
                            : ChartType.Bar,
                        );
                      }}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={currentVulnChartsOpen}>
            <CardBody>
              <div className="row">
                <Suspense fallback={<LoadingScreen />}>
                  <CurrentCharts
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </div>
            </CardBody>
          </Collapse>
        </Card>
        <VulnerabilityMetrics />
      </div>
      <div ref={listingScrollRef} aria-hidden></div>
      <ErrorBoundary>
        <SAST2 />
      </ErrorBoundary>
      <ModalDialog
        title={modalComponentData?.title}
        subTitle={modalComponentData?.subTitle}
        childComponent={modalComponentData?.bodyComponent}
        onClose={() => {
          setModalComponentData(undefined);
        }}
      />
    </Fragment>
  );
};

export default SASTDashboard2;
